import type { ModuleKey } from '@fos/shared/enums';
import { MODULE_LABELS } from '@fos/shared/labels';
import {
  Bell,
  BookOpen,
  Bot,
  CalendarDays,
  Clock,
  FileText,
  FolderKanban,
  History,
  Inbox,
  KeyRound,
  Link2,
  Lock,
  type LucideIcon,
  NotebookPen,
  Receipt,
  Server,
  ShieldCheck,
  Sparkles,
  SquareCheckBig,
  Sun,
  Timer,
  User,
  UserPlus,
  Users,
  Workflow,
} from 'lucide-react';

export interface NavItem {
  key: ModuleKey | 'today' | 'offers' | 'meetings';
  to: string;
  label: string;
  icon: LucideIcon;
  /** null = zawsze widoczne („Dziś”). */
  module: ModuleKey | null;
}

/** Menu boczne (DESIGN §4, Figma Sidebar v2 3017:1854). */
export const PRIMARY_NAV: NavItem[] = [
  { key: 'today', to: '/dzis', label: 'Dziś', icon: Sun, module: null },
  { key: 'inbox', to: '/inbox', label: MODULE_LABELS.inbox, icon: Inbox, module: 'inbox' },
  { key: 'approvals', to: '/akceptacje', label: MODULE_LABELS.approvals, icon: ShieldCheck, module: 'approvals' },
  { key: 'tasks', to: '/zadania', label: MODULE_LABELS.tasks, icon: SquareCheckBig, module: 'tasks' },
  { key: 'clients', to: '/klienci', label: MODULE_LABELS.clients, icon: Users, module: 'clients' },
  { key: 'time', to: '/czas', label: MODULE_LABELS.time, icon: Timer, module: 'time' },
];

/** Grupa „Więcej”. */
export const MORE_NAV: NavItem[] = [
  { key: 'projects', to: '/projekty', label: MODULE_LABELS.projects, icon: FolderKanban, module: 'projects' },
  { key: 'meetings', to: '/spotkania', label: 'Spotkania', icon: CalendarDays, module: 'clients' },
  { key: 'maintenance', to: '/utrzymanie', label: MODULE_LABELS.maintenance, icon: Server, module: 'maintenance' },
  { key: 'billing', to: '/rozliczenia', label: MODULE_LABELS.billing, icon: Receipt, module: 'billing' },
  { key: 'knowledge', to: '/wiedza', label: MODULE_LABELS.knowledge, icon: BookOpen, module: 'knowledge' },
  { key: 'agents', to: '/agenci', label: MODULE_LABELS.agents, icon: Bot, module: 'agents' },
];

/** Tylko w ⌘K (bez pozycji w menu): lista ofert, moduł Klienci. Spotkania są w „Więcej” (prośba Sebastiana 9.10.2026). */
export const COMMAND_ONLY_NAV: NavItem[] = [
  { key: 'offers', to: '/oferty', label: 'Oferty', icon: FileText, module: 'clients' },
];

export const ALL_NAV: NavItem[] = [...PRIMARY_NAV, ...MORE_NAV, ...COMMAND_ONLY_NAV];

function primary(key: NavItem['key']): NavItem {
  const item = PRIMARY_NAV.find((i) => i.key === key);
  if (!item) throw new Error(`Brak pozycji menu: ${key}`);
  return item;
}

/**
 * Dolne menu na mobile (DECISIONS 2026-10-17 [P] „Telefon: łatwy dostęp do całego menu”): Dziś, Inbox, [mikrofon],
 * Zadania, [Menu]. Mikrofon i Menu to przyciski (MobileNav); Akceptacje i pozostałe moduły są w panelu Menu
 * (MobileMenu), a liczba akceptacji do decyzji — na przycisku Menu.
 */
export const MOBILE_NAV_LEFT: NavItem[] = [primary('today'), primary('inbox')];
export const MOBILE_NAV_RIGHT: NavItem[] = [primary('tasks')];

/** Pozycja „Akceptacje” — w panelu Menu na telefonie na samej górze, z liczbą do decyzji. */
export const APPROVALS_NAV: NavItem = primary('approvals');

/** Formularz modułu otwierany w miejscu (AppShell: AddDialogs). */
export type AddDialogKind = 'task' | 'time' | 'client' | 'project' | 'asset' | 'note' | 'meeting' | 'offer';

/**
 * Akcje „Dodaj…” (przycisk + w menu i ⌘K). Z `dialog` otwierają formularz modułu w miejscu (AppShell: AddDialogs),
 * bez niego prowadzą do modułu (`to`; ten adres otwiera też formularz na stronie modułu).
 */
export interface AddAction {
  key: string;
  label: string;
  icon: LucideIcon;
  to: string;
  module: ModuleKey;
  dialog?: AddDialogKind;
  keywords?: string[];
}

export const ADD_ACTIONS: AddAction[] = [
  {
    key: 'task',
    label: 'Nowe zadanie',
    icon: SquareCheckBig,
    to: '/zadania?nowe=1',
    module: 'tasks',
    dialog: 'task',
    keywords: ['dodaj'],
  },
  {
    key: 'time',
    label: 'Dodaj czas',
    icon: Clock,
    to: '/czas?nowy=1',
    module: 'time',
    dialog: 'time',
    keywords: ['wpis', 'godziny', 'czas pracy'],
  },
  {
    key: 'client',
    label: 'Nowy klient',
    icon: UserPlus,
    to: '/klienci?nowy=1',
    module: 'clients',
    dialog: 'client',
    keywords: ['dodaj'],
  },
  {
    key: 'project',
    label: 'Nowy projekt',
    icon: FolderKanban,
    to: '/projekty',
    module: 'projects',
    dialog: 'project',
    keywords: ['dodaj'],
  },
  {
    key: 'asset',
    label: 'Nowy zasób',
    icon: Server,
    to: '/utrzymanie',
    module: 'maintenance',
    dialog: 'asset',
    keywords: ['dodaj', 'strona', 'domena', 'utrzymanie'],
  },
  {
    key: 'note',
    label: 'Nowa notatka',
    icon: NotebookPen,
    to: '/wiedza?nowa=1',
    module: 'knowledge',
    dialog: 'note',
    keywords: ['dodaj', 'wiedza', 'decyzja'],
  },
  {
    key: 'meeting',
    label: 'Nowe spotkanie',
    icon: CalendarDays,
    to: '/spotkania?nowe=1',
    module: 'clients',
    dialog: 'meeting',
    keywords: ['dodaj', 'spotkania', 'transkrypcja'],
  },
  {
    key: 'offer',
    label: 'Nowa oferta',
    icon: FileText,
    to: '/oferty?nowa=1',
    module: 'clients',
    dialog: 'offer',
    keywords: ['dodaj', 'oferty', 'wycena'],
  },
];

/**
 * Akcje kontekstowe dla najlepiej trafionego klienta w ⌘K, jak w Figmie 13: trzy akcje nad wynikami („Dodaj czas
 * dla REA”, „Nowe zadanie dla REA”, trzecia — mail do kontaktu — dojdzie z pocztą; do tego czasu „Nowe spotkanie”).
 * Projekt, zasób, notatkę i ofertę klienta dodaje się z jego profilu albo z ogólnych akcji („+”, „nowy projekt”).
 */
export const CLIENT_CONTEXT_ACTIONS: AddDialogKind[] = ['time', 'task', 'meeting'];

/** Podstrony Ustawień (Figma: 12 Ustawienia). */
export const SETTINGS_NAV: Array<{ to: string; label: string; icon: LucideIcon }> = [
  { to: '/ustawienia/profil', label: 'Profil', icon: User },
  { to: '/ustawienia/zespol', label: 'Zespół', icon: Users },
  { to: '/ustawienia/integracje', label: 'Integracje', icon: Link2 },
  { to: '/ustawienia/ai', label: 'AI i pamięć', icon: Sparkles },
  // Fala 5: tokeny osobiste MCP, komputery z runnerem i instrukcja podłączenia Claude Code
  { to: '/ustawienia/tokeny', label: 'Tokeny i agenci', icon: KeyRound },
  { to: '/ustawienia/powiadomienia', label: 'Powiadomienia', icon: Bell },
  // Fala 10: reguły „gdy … → zrób …” (powiadomienia, zadania, diagnoza Claude, poranny brief)
  { to: '/ustawienia/automatyzacje', label: 'Automatyzacje', icon: Workflow },
  { to: '/ustawienia/bezpieczenstwo', label: 'Bezpieczeństwo', icon: Lock },
  { to: '/ustawienia/audyt', label: 'Audyt', icon: History },
];
